import React, { useEffect, useRef, useState } from 'react';

/* eslint-disable react/prop-types */
const inputClassName = 'mt-2 box-border h-8 w-full max-w-full min-w-0 rounded-md border border-gray-200 bg-white px-2 text-xs font-normal normal-case tracking-normal text-current focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20';

const readRangeValue = (value) => (Array.isArray(value) ? value : [null, null]);

const TextFilter = ({ column, config }) => {
  const filterValue = String(column.getFilterValue() ?? '');
  const [textValue, setTextValue] = useState(filterValue);

  // `column` is a fresh object from useReactTable() on every parent
  // re-render (e.g. while a backend refetch is in flight), even when the
  // actual filter column hasn't changed. Keeping it out of the debounce
  // effect's dependency array - via a ref instead - stops a re-render
  // mid-typing from clearing/restarting the pending timeout, which
  // otherwise meant the debounced commit could keep getting reset and
  // never actually fire.
  const columnRef = useRef(column);
  columnRef.current = column;
  const filterValueRef = useRef(filterValue);
  filterValueRef.current = filterValue;

  useEffect(() => setTextValue(filterValue), [filterValue]);

  useEffect(() => {
    if (textValue === filterValueRef.current) return undefined;
    const timeout = setTimeout(() => columnRef.current.setFilterValue(textValue || undefined), 500);
    return () => clearTimeout(timeout);
  }, [textValue]);

  return (
    <input
      type="search"
      aria-label={`Filter ${config.label || column.id}`}
      value={textValue}
      onChange={(event) => setTextValue(event.target.value)}
      onClick={(event) => event.stopPropagation()}
      placeholder={config.placeholder || 'Filter'}
      className={`${inputClassName} placeholder:text-gray-400`}
    />
  );
};

const SelectFilter = ({ column, config }) => (
  <select
    aria-label={`Filter ${config.label || column.id}`}
    value={String(column.getFilterValue() ?? '')}
    onChange={(event) => column.setFilterValue(event.target.value || undefined)}
    onClick={(event) => event.stopPropagation()}
    // listSize (optional): when a column has more options than fit
    // comfortably in a native dropdown popup, setting this renders the
    // select as an open, fixed-height listbox that scrolls past that many
    // rows instead of relying on the browser's own (inconsistent) popup
    // sizing.
    {...(config.listSize ? { size: config.listSize } : {})}
    className={`${inputClassName}${config.listSize ? ' overflow-y-auto' : ''}`}
  >
    <option value="">{config.placeholder || 'All'}</option>
    {(config.options || []).map((option) => (
      <option key={option.value} value={option.value}>{option.label}</option>
    ))}
  </select>
);

const ComboboxFilter = ({ column, config }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const closeTimer = useRef(null);
  const options = config.options || [];
  const selectedValue = column.getFilterValue();
  const selectedLabel = options.find((o) => o.value === selectedValue)?.label ?? '';
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;
  const maxVisible = config.maxVisible || 4;
  const rowHeightPx = 28;

  const commit = (value) => {
    column.setFilterValue(value || undefined);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="relative mt-2">
      <input
        type="text"
        aria-label={`Filter ${config.label || column.id}`}
        value={open ? query : selectedLabel}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setQuery('');
          setOpen(true);
        }}
        onBlur={() => {
          // Let an option's onMouseDown-triggered click register before the
          // list unmounts - a plain onClick would lose the race to blur.
          closeTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onClick={(event) => event.stopPropagation()}
        placeholder={config.placeholder || 'Search…'}
        className={`${inputClassName} placeholder:text-gray-400`}
      />
      {open && (
        <div
          className="absolute left-0 right-0 z-20 mt-1 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
          style={{ maxHeight: `${rowHeightPx * maxVisible}px` }}
          onMouseDown={(event) => {
            event.preventDefault();
            clearTimeout(closeTimer.current);
          }}
        >
          <button
            type="button"
            onClick={() => commit(undefined)}
            className="block w-full px-2 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50"
          >
            {config.placeholder || 'All'}
          </button>
          {filtered.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-gray-400">No matches</p>
          ) : (
            filtered.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => commit(option.value)}
                className={`block w-full px-2 py-1.5 text-left text-xs hover:bg-gray-50 ${
                  option.value === selectedValue ? 'bg-brand/5 font-medium text-brand' : 'text-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const MultiSelectFilter = ({ column, config }) => {
  const selectedValues = column.getFilterValue() || [];

  return (
    <select
      multiple
      aria-label={`Filter ${config.label || column.id}`}
      value={selectedValues}
      onChange={(event) => {
        const values = Array.from(event.target.selectedOptions, (option) => option.value);
        column.setFilterValue(values.length ? values : undefined);
      }}
      onClick={(event) => event.stopPropagation()}
      className={`${inputClassName} h-16`}
    >
      {(config.options || []).map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
};

const NumberFilter = ({ column, config }) => (
  <input
    type="number"
    aria-label={`Filter ${config.label || column.id}`}
    value={column.getFilterValue() ?? ''}
    onChange={(event) => column.setFilterValue(event.target.value === '' ? undefined : Number(event.target.value))}
    onClick={(event) => event.stopPropagation()}
    placeholder={config.placeholder || 'Filter'}
    className={inputClassName}
  />
);

const DateFilter = ({ column, config }) => (
  <input
    type="date"
    aria-label={`Filter ${config.label || column.id}`}
    value={column.getFilterValue() ?? ''}
    onChange={(event) => column.setFilterValue(event.target.value || undefined)}
    onClick={(event) => event.stopPropagation()}
    className={inputClassName}
  />
);

const RangeFilter = ({ column, config, type }) => {
  const value = readRangeValue(column.getFilterValue());
  const label = config.label || column.id;

  const updateBound = (index, rawValue) => {
    const nextValue = [...value];
    nextValue[index] = rawValue === '' ? null : type === 'number' ? Number(rawValue) : rawValue;
    column.setFilterValue(nextValue.every((bound) => bound === null) ? undefined : nextValue);
  };

  return (
    <div className="mt-2 flex w-full min-w-0 gap-1">
      {['Min', 'Max'].map((boundLabel, index) => (
        <input
          key={boundLabel}
          type={type}
          aria-label={`${boundLabel} ${label}`}
          value={value[index] ?? ''}
          onChange={(event) => updateBound(index, event.target.value)}
          onClick={(event) => event.stopPropagation()}
          placeholder={boundLabel}
          className="box-border h-8 w-1/2 min-w-0 rounded-md border border-gray-200 bg-white px-2 text-xs font-normal normal-case tracking-normal text-current focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
      ))}
    </div>
  );
};

const BooleanFilter = ({ column, config }) => (
  <select
    aria-label={`Filter ${config.label || column.id}`}
    value={column.getFilterValue() === undefined ? '' : String(column.getFilterValue())}
    onChange={(event) => {
      const { value } = event.target;
      column.setFilterValue(value === '' ? undefined : value === 'true');
    }}
    onClick={(event) => event.stopPropagation()}
    className={inputClassName}
  >
    <option value="">{config.placeholder || 'All'}</option>
    <option value="true">{config.trueLabel || 'Yes'}</option>
    <option value="false">{config.falseLabel || 'No'}</option>
  </select>
);

const filterRenderers = {
  text: TextFilter,
  select: SelectFilter,
  combobox: ComboboxFilter,
  'multi-select': MultiSelectFilter,
  number: NumberFilter,
  'number-range': (props) => <RangeFilter {...props} type="number" />,
  date: DateFilter,
  'date-range': (props) => <RangeFilter {...props} type="date" />,
  boolean: BooleanFilter,
};

const DataTableFilter = ({ column }) => {
  const config = column.columnDef.meta?.filter;
  if (!config || !column.getCanFilter()) return null;

  const Filter = filterRenderers[config.variant];
  if (!Filter) return null;

  return <Filter column={column} config={config} />;
};

export default DataTableFilter;
